import { measureGeneration } from '~/server/static-metrics'
import { GetStaticProps, NextPage } from 'next'
import { useState } from 'react'
import { FaHistory } from 'react-icons/fa'
import InfiniteScroll from 'react-infinite-scroller'
import { ArticleList } from 'web_core/assets/article/List'
import { LoadingSpinner } from 'web_core/assets/spinners'
import { ESArticle } from 'web_core/elastic_search'
import { fb } from 'web_core/firebase'
import { useESArticles } from 'web_core/hooks/models'
import { Serialized, deserializeArray, isBlank } from 'web_core/utils'
import { addLocaleProps } from 'web_core/utils/static_props'
import { DefaultLayout } from '~/components/Layout/Default'
import { ContentNavSelector } from '~/components/Utils/ContentNavSelector'
import { FollowingNavSelector } from '~/components/Utils/FollowingNavSelector'
import { Heading } from '~/components/Utils/Heading'
import { SearchFilter } from '~/components/Utils/SearchFilter'
import { useLayoutProperty } from '~/context/layout_property'
import {
  LayoutStaticProps,
  getCategoriesStaticProps,
  getPopularProfilesStaticProps,
} from '~/static_props/layout'
import { articleTimelineBreadcrumb } from '~/utils'

type Props = {
  articles: Serialized<ESArticle>[]
} & LayoutStaticProps

export const getStaticProps: GetStaticProps<Props> = async ({
  locale = 'ja',
}) =>
  measureGeneration('timeline_articles', async (measure) => {
    const { data } = await measure(() =>
      fb.call('search')({
        index: 'articles',
        params: { type: 'timeline', mode: 'all', locale: 'ja' },
      })
    )
    if (data.index !== 'articles') {
      throw new Error('Unexpected response.')
    }
    const articles = data.result
    const result = {
      props: {
        articles,
        categories: await measure(() => getCategoriesStaticProps()),
        popularProfiles: await measure(() => getPopularProfilesStaticProps()),
      },
      revalidate: 10,
    }
    return addLocaleProps(result, locale)
  })

const Page: NextPage<Props> = ({
  articles: staticArticles,
  categories,
  popularProfiles,
}) => {
  const [categoryId, setCategoryId] = useState<string>()
  const { articles, loadMore, isReachingEnd, isLoadingMore } = useESArticles(
    {
      type: 'timeline',
      mode: 'all',
      locale: 'ja',
      category_id:
        !isBlank(categoryId) && categoryId !== '' ? categoryId : undefined,
    },
    deserializeArray(staticArticles, {})
  )

  const { headerHeight } = useLayoutProperty()
  return (
    <DefaultLayout
      head={{
        title: '記事のタイムライン',
        description: 'Mathlogに投稿された記事を新着順でご覧いただけます。',
      }}
      leftSidebar
      rightSideBar
      categoriesMenu={{ pc: true, sp: true }}
      staticCategories={categories}
      staticPopularProfiles={popularProfiles}
      navLinks={{ pc: true, tablet: true, sp: true, active: 'timeline' }}
    >
      {articleTimelineBreadcrumb.toReactNode()}
      <Heading title="タイムライン" icon={FaHistory} className="mb-0" />
      <div
        className="sticky-top bg-white pt-2 pb-2 mb-2"
        style={{ top: headerHeight }}
      >
        <ContentNavSelector
          active="articles"
          link="/timeline/all_[contents]"
          className="mb-2 mb-md-4"
        />
        <div className="d-flex justify-content-between gap-4">
          <FollowingNavSelector
            active="all"
            link="/timeline/[prefix]articles"
            className="flex-shrink-0 fs-7 fs-md-6"
          />
          <SearchFilter
            categoryFilter={{ onSelectId: setCategoryId, id: categoryId }}
            className="fs-7 fs-md-6"
          />
        </div>
      </div>
      <InfiniteScroll
        loadMore={async () => {
          if (isLoadingMore === false && isReachingEnd === false)
            await loadMore()
        }}
        hasMore={isReachingEnd === false}
        loader={<LoadingSpinner />}
      >
        {articles && (
          <ArticleList
            articles={articles}
            className="grid g-cols-1 g-lg-cols-2"
          />
        )}
      </InfiniteScroll>
    </DefaultLayout>
  )
}

export default Page
